/**
 * Sesja wsparcia po stronie przeglądarki — stan, nagrywanie, zakończenie.
 * =======================================================================
 *
 * Gdy partner wykupi sześciocyfrowy kod (`support_access_kod_uzyj`, migracja
 * 459), dostaje CZASOWE członkostwo w organizacji najemcy i od tej chwili widzi
 * dokładnie to, co widzi najemca. Warunkiem, na który zgodził się najemca, jest
 * jedno: cała sesja ma być nagrana. Ten plik jest tym nagraniem.
 *
 * ─── Co nagrywamy i dlaczego akurat to ──────────────────────────────────────
 *
 *   • `nawigacja` — każde wejście na ekran (adres trasy). To odpowiada na
 *     pytanie „co on w ogóle oglądał", którego `audit_logs` nie zna, bo audyt
 *     rejestruje ZMIANY, a nie patrzenie.
 *   • `odczyt`    — sięgnięcie po tabelę, ziarnistość „ekran + tabela": jeden
 *     wpis na parę, nie na zapytanie i nie na wiersz (patrz komentarz przy
 *     `widzianeOdczyty`). Dzięki temu najemca widzi, w co partner zaglądał,
 *     a dziennik da się przeczytać.
 *   • `zmiana`    — każdy zapis do bazy (insert/update/upsert/delete) wykonany
 *     w czasie sesji, wraz z nazwą tabeli i trasą, z której poszedł.
 *   • `akcja`     — to, co ekran zgłosi sam (np. „wysłano e-mail do klienta").
 *
 * Wartości pól PRZED i PO nie idą do dziennika sesji celowo: trzyma je
 * `audit_logs` (kolumny `old_values`/`new_values`), który jest kanonicznym
 * śladem danych i ma własną retencję. Duplikowanie ich tutaj oznaczałoby dwie
 * kopie danych osobowych klienta w dwóch tabelach o różnych politykach —
 * dziennik sesji wiąże zmianę z wejściem partnera, a nie zastępuje audytu.
 *
 * ─── Dlaczego instrumentujemy klienta Supabase, a nie ekrany ────────────────
 *
 * Zapisów do bazy dokonuje kilkaset miejsc w powłoce i w modułach. Dopisanie
 * `logujZmiane()` do każdego z nich byłoby niekompletne w dniu napisania i
 * martwe dwa tygodnie później — a dziennik, który pomija część zmian, jest
 * gorszy niż brak dziennika, bo wygląda na kompletny. Dlatego opakowujemy
 * `supabase.from(...)` JEDEN raz: instrumentacja jest aktywna wyłącznie podczas
 * trwającej sesji wsparcia i przy jej braku nie robi nic poza zwróceniem
 * oryginalnego budowniczego.
 *
 * Nagrywanie jest fail-open: błąd zapisu zdarzenia nie może wywrócić operacji,
 * którą partner właśnie wykonuje dla klienta.
 */

import type { SupportAccessScope } from '@insolitum/types';

import { supabase } from './supabase';

const KLUCZ = 'insolitum.support_session';

export interface AktywnaSesjaWsparcia {
  sessionId: string;
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  scope: SupportAccessScope;
  expiresAt: string;
  /**
   * `zgoda` — partner wykupił kod najemcy; `serwis` — super-admin wszedł bez
   * kodu z powodem (migracja 837). Brak pola = sesja zapisana przed 837,
   * czyli `zgoda`.
   */
  rodzaj?: 'zgoda' | 'serwis';
}

type Sluchacz = (sesja: AktywnaSesjaWsparcia | null) => void;

const sluchacze = new Set<Sluchacz>();
let biezaca: AktywnaSesjaWsparcia | null = odczytajZPamieci();

function odczytajZPamieci(): AktywnaSesjaWsparcia | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(KLUCZ);
    if (!raw) return null;
    const sesja = JSON.parse(raw) as AktywnaSesjaWsparcia;
    // Sesja z przeszłości nie jest sesją. Baza i tak odmówi zapisu zdarzenia,
    // ale banner nie ma prawa twierdzić, że wejście trwa.
    if (Date.parse(sesja.expiresAt) <= Date.now()) {
      window.sessionStorage.removeItem(KLUCZ);
      return null;
    }
    return sesja;
  } catch {
    return null;
  }
}

function rozglos(): void {
  for (const s of sluchacze) s(biegnacaSesja());
}

/** Aktualna sesja albo `null`. Wygasła sesja jest traktowana jak brak sesji. */
export function biegnacaSesja(): AktywnaSesjaWsparcia | null {
  if (biezaca && Date.parse(biezaca.expiresAt) <= Date.now()) {
    biezaca = null;
    try {
      window.sessionStorage.removeItem(KLUCZ);
    } catch {
      /* pamięć sesji może być niedostępna — stan w RAM już wyczyszczony */
    }
  }
  return biezaca;
}

export function subskrybujSesje(sluchacz: Sluchacz): () => void {
  sluchacze.add(sluchacz);
  return () => sluchacze.delete(sluchacz);
}

export function zapamietajSesje(sesja: AktywnaSesjaWsparcia): void {
  biezaca = sesja;
  widzianeOdczyty.clear();
  try {
    window.sessionStorage.setItem(KLUCZ, JSON.stringify(sesja));
  } catch {
    /* brak sessionStorage nie może przerwać wejścia — sesja żyje w RAM */
  }
  wlaczInstrumentacje();
  rozglos();
}

export function zapomnijSesje(): void {
  biezaca = null;
  widzianeOdczyty.clear();
  try {
    window.sessionStorage.removeItem(KLUCZ);
  } catch {
    /* jak wyżej */
  }
  rozglos();
}

// ─────────────────────────────────────────────────────────────────────────────
// Zapis zdarzeń
// ─────────────────────────────────────────────────────────────────────────────

export type RodzajZdarzenia = 'nawigacja' | 'odczyt' | 'zmiana' | 'akcja' | 'blad';

export interface ZdarzenieWejscie {
  kind: RodzajZdarzenia;
  route?: string;
  action?: string;
  entityType?: string;
  entityId?: string | null;
  summary?: string;
  payload?: Record<string, unknown>;
}

/**
 * Dopisuje zdarzenie do dziennika sesji. Bez aktywnej sesji nie robi nic —
 * dzięki temu wywołanie z dowolnego ekranu jest bezpieczne także wtedy, gdy
 * ekran ogląda sam najemca.
 */
export async function nagrajZdarzenie(z: ZdarzenieWejscie): Promise<void> {
  const sesja = biegnacaSesja();
  if (!sesja) return;
  try {
    const { data, error } = await supabase.rpc('support_access_zdarzenie', {
      p_session_id: sesja.sessionId,
      p_kind: z.kind,
      p_route: z.route ?? (typeof window !== 'undefined' ? window.location.pathname : null),
      p_action: z.action ?? null,
      p_entity_type: z.entityType ?? null,
      p_entity_id: z.entityId ?? null,
      p_summary: z.summary ?? null,
      p_payload: z.payload ?? {},
    });
    // `NULL` zamiast identyfikatora zdarzenia znaczy „ta sesja już nie działa"
    // — nieznana, zamknięta albo po terminie (migracja 462). Baza właśnie
    // zdjęła partnerowi czasowe członkostwo, więc baner musi zniknąć razem
    // z dostępem. Wyjątku tam CELOWO nie ma: cofnąłby to sprzątanie.
    if (!error && data === null) {
      zapomnijSesje();
      return;
    }
    // Starsze wdrożenie bazy (przed 462) sygnalizuje to jeszcze wyjątkiem.
    if (error && /wygasla|zamknieta|Nieznana sesja/i.test(error.message)) {
      zapomnijSesje();
    }
  } catch {
    /* fail-open: nagranie nie może przerwać pracy partnera u klienta */
  }
}

/** Zamyka sesję w bazie (zdejmuje czasowe członkostwo) i czyści stan lokalny. */
export async function zakonczSesje(
  reason: 'partner' | 'najemca' | 'admin' = 'partner'
): Promise<void> {
  const sesja = biegnacaSesja();
  if (!sesja) return;
  try {
    await supabase.rpc('support_access_zakoncz', {
      p_session_id: sesja.sessionId,
      p_reason: reason,
    });
  } finally {
    zapomnijSesje();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Instrumentacja klienta Supabase — nagrywanie ODCZYTÓW i ZMIAN
// ─────────────────────────────────────────────────────────────────────────────

type Budowniczy = ReturnType<typeof supabase.from>;
type FromFn = (relation: string) => Budowniczy;

let zainstrumentowany = false;

/** Zapisy do tych tabel to samo nagrywanie — logowanie ich zapętliłoby dziennik. */
const POMIJANE_TABELE = new Set([
  'support_access_events',
  'support_access_sessions',
  'support_access_grants',
  'audit_logs',
]);

const CZASOWNIKI = ['insert', 'update', 'upsert', 'delete'] as const;
type Czasownik = (typeof CZASOWNIKI)[number];

/**
 * Odczyty: JEDEN wpis na parę „ekran + tabela", nie na wiersz i nie na
 * zapytanie.
 *
 * Najemca ma wiedzieć, w co partner zaglądał — i to zdanie spełnia ziarno
 * „na ekranie X sięgnął po tabelę Y". Zapisywanie każdego `select` osobno dałoby
 * kilkaset wierszy na jedną sesję (jeden ekran potrafi odpytać bazę kilkanaście
 * razy przy każdym renderze), a dziennik, przez który nie da się przejść
 * wzrokiem, nie jest kontrolą — jest hałasem. Wartości wierszy nie idą do
 * dziennika w ogóle: to są dane osobowe klienta, a od ich śladu jest
 * `audit_logs` z własną retencją.
 */
const widzianeOdczyty = new Set<string>();
/** Bezpiecznik na sesję: tyle różnych par ekran+tabela i ani jednej więcej. */
const LIMIT_ODCZYTOW = 300;

/**
 * Opakowuje `supabase.from` jeden raz na życie karty. Gdy sesji nie ma,
 * zwracany jest oryginalny budowniczy bez żadnego narzutu.
 */
export function wlaczInstrumentacje(): void {
  if (zainstrumentowany) return;
  zainstrumentowany = true;

  const oryginalneFrom = supabase.from.bind(supabase) as FromFn;

  (supabase as unknown as { from: FromFn }).from = (relation: string): Budowniczy => {
    const budowniczy = oryginalneFrom(relation);
    if (!biegnacaSesja() || POMIJANE_TABELE.has(relation)) return budowniczy;

    const oryginalnySelect = (budowniczy as unknown as Record<string, unknown>).select;
    if (typeof oryginalnySelect === 'function') {
      (budowniczy as unknown as Record<string, unknown>).select = (...args: unknown[]) => {
        const trasa = typeof window !== 'undefined' ? window.location.pathname : '';
        const klucz = `${trasa}|${relation}`;
        if (!widzianeOdczyty.has(klucz) && widzianeOdczyty.size < LIMIT_ODCZYTOW) {
          widzianeOdczyty.add(klucz);
          void nagrajZdarzenie({
            kind: 'odczyt',
            action: 'select',
            route: trasa || undefined,
            entityType: relation,
            summary: `Podgląd danych z tabeli ${relation}`,
          });
        }
        return (oryginalnySelect as (...a: unknown[]) => unknown).apply(budowniczy, args);
      };
    }

    for (const czasownik of CZASOWNIKI) {
      const oryginalna = (budowniczy as unknown as Record<string, unknown>)[czasownik];
      if (typeof oryginalna !== 'function') continue;

      (budowniczy as unknown as Record<string, unknown>)[czasownik] = (...args: unknown[]) => {
        // Zdarzenie idzie ZARAZ, a nie po odpowiedzi: budowniczy PostgREST jest
        // „thenable", więc czekanie na wynik wymagałoby jego opakowania i
        // zmieniłoby semantykę łańcucha (.select().single() itd.). Dziennik ma
        // odpowiadać na „co próbował zrobić", a skutek zapisu i tak zostaje
        // w `audit_logs`.
        void nagrajZdarzenie({
          kind: 'zmiana',
          action: czasownik,
          entityType: relation,
          summary: `${czasownikPoPolsku(czasownik)} w tabeli ${relation}`,
        });
        return (oryginalna as (...a: unknown[]) => unknown).apply(budowniczy, args);
      };
    }

    return budowniczy;
  };
}

function czasownikPoPolsku(c: Czasownik): string {
  switch (c) {
    case 'insert':
      return 'Dodanie rekordu';
    case 'update':
      return 'Zmiana rekordu';
    case 'upsert':
      return 'Zapis rekordu';
    case 'delete':
      return 'Usunięcie rekordu';
  }
}

// Karta odświeżona w środku sesji musi dalej nagrywać.
if (typeof window !== 'undefined' && biezaca) {
  wlaczInstrumentacje();
}
